import Breadcrumbs from "@/components/Breadcrumbs";
import ClassificationTableBlockTool from "@/components/EditorJs/blocks/ClassificationTableBlock";
import ContentBlock from "@/components/EditorJs/blocks/ContentBlock";
import DocumentationTableBlockTool from "@/components/EditorJs/blocks/DocumentationTableBlock";
import GeometryTableBlockTool from "@/components/EditorJs/blocks/GeometryTableBlock";
import HeaderBlock from "@/components/EditorJs/blocks/HeaderBlock";
import IfcTableBlockTool from "@/components/EditorJs/blocks/IfcTableBlock";
import InformationTableBlockTool from "@/components/EditorJs/blocks/InformationTableBlock";
import { TemplateService } from "@/services/admin-services";
import { Template, TemplateContent } from "@/services/admin-services/models";
import { LocaleService } from "@/services/global-services";
import { Language, Locale, Software } from "@/services/global-services/models";
import { LogLevels } from "@/utils/Utils";
import EditorJS, { OutputData, ToolConstructable } from "@editorjs/editorjs";
import Paragraph from "@editorjs/paragraph";
import { PaperAirplaneIcon, XMarkIcon } from "@heroicons/react/24/outline";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Collapse,
    Divider,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
    useTheme,
} from "@mui/material";
import { nanoid } from "nanoid";
import { MutableRefObject, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

const EDITOR_CONTAINER_ID = "editorjs-template-container";

const breadcrumbs = [
    {
        to: "../..",
        title: "Administration",
    },
    {
        title: "Templates",
        to: "..",
    },
];

const Editor = () => {
    const theme = useTheme();

    const [breadcrumbTrail, setBreadcrumbTrail] = useState(breadcrumbs);
    const [languages, setLanguages] = useState<Language[]>([]);

    const editorRef = useRef<EditorJS | null>(null);

    const [selectedLanguage, setSelectedLanguage] = useState("int-en");

    const [loading, setLoading] = useState<boolean>(true);
    const [editorLoaded, setEditorLoaded] = useState<boolean>(false);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [savedNotificationOpen, setSavedNotificationOpen] = useState<boolean>(false);
    const timerRef = useRef<NodeJS.Timeout | null>(null);

    const [templateName, setTemplateName] = useState<string>("");
    const [software, setSoftware] = useState<Software[]>([]);
    const [classifications, setClassifications] = useState<Software[]>([]);

    const localeService = new LocaleService("int", "en");
    const templateService = new TemplateService("int");

    const [template, setTemplate] = useState<Template>();
    const [templateContent, setTemplateContent] = useState<{ [key: string]: TemplateContent | undefined }>({});
    const [templateEditor, setTemplateEditor] = useState<OutputData>({
        time: new Date().getTime(),
        blocks: [],
    });

    const { identifier } = useParams();
    const navigate = useNavigate();

    useEffect(() => {
        LocaleService.get().then(parseLanguages);
        localeService.getSoftware("wip").then((software) => {
            const actualSoftware = software.filter((x) => x.type === "DataStructure");
            const classifications = software.filter((x) => x.isClassification);

            setSoftware(actualSoftware);
            setClassifications(classifications);
        });

        return () => {
            // eslint-disable-next-line react-hooks/exhaustive-deps
            const editor = editorRef.current;
            if (editor?.destroy) {
                editor.destroy();
            }
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!savedNotificationOpen) return;

        timerRef.current = setTimeout(() => setSavedNotificationOpen(false), 3000);
    }, [savedNotificationOpen]);

    useEffect(() => {
        if (loading || !editorLoaded) return;

        const content = templateContent[selectedLanguage];
        let editorData: OutputData;
        if (!content)
            editorData = {
                time: new Date().getTime(),
                blocks: [
                    {
                        id: nanoid(),
                        type: "page-section-header",
                        data: {
                            text: "",
                            level: 2,
                        },
                    },
                ],
            };
        else
            editorData = {
                time: new Date().getTime(),
                blocks: content.sections.map((section) => {
                    let content = section.content;
                    try {
                        content = JSON.parse(content);
                    } catch {
                        if (section.sectionType === "page-section-header")
                            content = {
                                text: content,
                                level: 2,
                            };
                        else content = {};
                    }
                    return {
                        id: section.sectionCode,
                        type: section.sectionType,
                        data: content,
                    };
                }),
            };

        setTemplateEditor(editorData);
        editorRef.current?.isReady?.then(() => {
            editorRef.current?.render(editorData);
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editorLoaded, loading, selectedLanguage]);

    useEffect(() => {
        const sections = templateEditor.blocks.map((block) => {
            let sectionCode: string;
            const sectionContent = JSON.stringify(block.data);

            if (block.type === "content-editor") sectionCode = block.data.id;
            else sectionCode = block.id!;

            return { sectionCode, sectionType: block.type, content: sectionContent };
        });

        const currentContent = { ...templateContent };
        const order = sections.map((x) => x.sectionCode);
        currentContent[selectedLanguage] = {
            languageCode: selectedLanguage,
            content: JSON.stringify(order),
            sections,
        };

        setTemplateContent(currentContent);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [templateEditor]);

    useEffect(() => {
        if (loading || software.length === 0 || editorLoaded || editorRef.current) return;

        initEditor(EDITOR_CONTAINER_ID, editorRef);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editorLoaded, loading, software]);

    useEffect(() => {
        if (!identifier) {
            setTemplate({ id: "", name: "", availableLanguages: [] });

            const content = {} as { [key: string]: TemplateContent | undefined };
            languages.forEach((x) => (content[x.slug] = undefined));

            setTemplateContent(content);
            setBreadcrumbTrail([
                ...breadcrumbs,
                {
                    to: "",
                    title: "New Template",
                },
            ]);
            setLoading(false);
        } else if (identifier) {
            setLoading(true);
            setEditorLoaded(false);
            templateService.getTemplate(identifier).then((template) => {
                if (!template) {
                    navigate("../new");
                    return;
                }

                const content = {} as { [key: string]: TemplateContent | undefined };
                languages.forEach((x) => (content[x.slug] = undefined));

                const contentPromises: Array<Promise<TemplateContent | undefined>> = [];
                template.availableLanguages.forEach((x) =>
                    contentPromises.push(
                        templateService
                            .getTemplateContent(template.id, x)
                            .then((templateContent) => (content[x] = templateContent))
                    )
                );

                Promise.all(contentPromises).then(() => {
                    setTemplateName(template.name);
                    setTemplate(template);
                    setTemplateContent(content);
                    setBreadcrumbTrail([
                        ...breadcrumbs,
                        {
                            title: "Saved Templates",
                            to: "..",
                        },
                        {
                            to: "",
                            title: template.name,
                        },
                    ]);
                    setLoading(false);
                });
            });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [identifier]);

    const parseLanguages = (regions: Locale[]) => {
        const languages: Language[] = [];
        for (let i = 0; i < regions.length; i++) {
            const region = regions[i];

            for (let j = 0; j < region.languages.length; j++) {
                const language = region.languages[j];
                if (languages.findIndex((x) => x.label === language.label) > -1) continue;

                const split = language.slug.split("-");
                languages.push({
                    ...language,
                    slug: `int-${split[1]}`,
                    regionLabel: "International",
                });
            }
        }

        setLanguages(languages.sort((a, b) => a.label.localeCompare(b.label)));
    };

    const initEditor = (id: string, ref: MutableRefObject<EditorJS | null>) => {
        const editor: EditorJS = new EditorJS({
            autofocus: false,
            logLevel: LogLevels.ERROR,
            holder: id,
            data: templateEditor,
            onReady: () => setEditorLoaded(true),
            onChange: async (api) => {
                const content = await api.saver.save();
                setTemplateEditor(content);
            },
            defaultBlock: "page-section-header",
            tools: {
                // Override paragraph which is added by default
                paragraph: false as unknown as ToolConstructable,
                "page-section-header": {
                    class: HeaderBlock as ToolConstructable,
                    config: {
                        defaultLevel: 2,
                        levels: [2],
                        placeholder: "Heading text",
                    },
                },
                "page-static-content": {
                    class: Paragraph as ToolConstructable,
                    inlineToolbar: true,
                    config: {
                        placeholder: "Enter text",
                    },
                },
                "content-editor": {
                    class: ContentBlock,
                },
                "ids-platform-table-ifc": {
                    class: IfcTableBlockTool,
                    config: {
                        currentLanguage: selectedLanguage,
                        software,
                        classifications,
                    },
                },
                "ids-platform-table-gid-geometry": {
                    class: GeometryTableBlockTool,
                    config: {
                        currentLanguage: selectedLanguage,
                    },
                },
                "ids-platform-table-gid-information": {
                    class: InformationTableBlockTool,
                    config: {
                        currentLanguage: selectedLanguage,
                        software,
                    },
                },
                "ids-platform-table-gid-documentation": {
                    class: DocumentationTableBlockTool,
                    config: {
                        currentLanguage: selectedLanguage,
                    },
                },
                "ids-platform-table-classification": {
                    class: ClassificationTableBlockTool,
                    config: {
                        currentLanguage: selectedLanguage,
                    },
                },
            },
        });

        ref.current = editor;
    };

    const onSave = () => {
        setIsSaving(true);
        if (!identifier) {
            templateService.createTemplate(templateName).then((template) => onSaveContent(template));
        } else {
            templateService.updateTemplate(identifier, templateName).then((template) => onSaveContent(template));
        }
    };

    const onSaveContent = (newTemplate: Template) => {
        const promises: Array<Promise<TemplateContent | undefined>> = [];
        for (let i = 0; i < languages.length; i++) {
            const slug = languages[i].slug;
            const content = templateContent[slug]!;

            if (content) {
                if (template && template.availableLanguages && template.availableLanguages.includes(slug)) {
                    promises.push(templateService.updateTemplateContent(newTemplate.id, slug, content));
                } else {
                    promises.push(templateService.createTemplateContent(newTemplate.id, slug, content));
                }
            }
        }

        Promise.all(promises).then(() => {
            setSavedNotificationOpen(true);
            window.scrollTo({ top: 0 });
            templateService.getTemplate(newTemplate.id).then(setTemplate);
            navigate(`../${newTemplate.id}`, { replace: true });
            setIsSaving(false);
        });
    };

    const onCancel = () => {
        navigate(`..`);
    };

    if (loading)
        return (
            <Box sx={{ display: "flex", height: 400, alignItems: "center", flexDirection: "column" }}>
                <Box sx={{ margin: "auto" }}>
                    <CircularProgress />
                </Box>
            </Box>
        );

    return (
        <Box>
            <Breadcrumbs trail={breadcrumbTrail} />

            <Divider />

            <Grid container spacing={2} p={{ xs: theme.spacing(2), md: theme.spacing(4) }}>
                <Box sx={{ width: "100%" }}>
                    <Collapse in={savedNotificationOpen}>
                        <Alert
                            action={
                                <IconButton
                                    aria-label="close"
                                    color="inherit"
                                    size="small"
                                    onClick={() => {
                                        setSavedNotificationOpen(false);
                                    }}
                                >
                                    <XMarkIcon className="h-5 w-5" />
                                </IconButton>
                            }
                            sx={{ mb: 2 }}
                        >
                            Changes to the template have been succesfully saved.
                        </Alert>
                    </Collapse>
                </Box>

                <Grid size={{ md: 12, lg: 4 }}>
                    <Typography variant="h6" gutterBottom fontWeight={700}>
                        Template information
                    </Typography>
                    <Typography variant="subtitle2" color={"text.secondary"}>
                        Define the name for a template to be used for element pages.
                    </Typography>
                </Grid>

                <Grid size={{ md: 12, lg: 8 }}>
                    <Grid container spacing={4}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Typography variant={"subtitle2"} sx={{ mb: 2 }} fontWeight={700}>
                                Enter a name for the template
                            </Typography>
                            <TextField
                                label="Name"
                                variant="outlined"
                                fullWidth
                                value={templateName}
                                onChange={(e) => setTemplateName(e.currentTarget.value)}
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>

            <Divider />

            <Grid container spacing={2} p={{ xs: theme.spacing(2), md: theme.spacing(4) }}>
                <Grid size={{ md: 12, lg: 4 }}>
                    <Typography variant="h6" gutterBottom fontWeight={700}>
                        Template layout
                    </Typography>
                    <Typography variant="subtitle2" color={"text.secondary"}>
                        Define the layout for the template in different languages
                    </Typography>
                </Grid>
                <Grid size={{ md: 12, lg: 8 }}>
                    <Grid container spacing={4}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <FormControl fullWidth>
                                <InputLabel id="demo-select-small">Language</InputLabel>
                                <Select
                                    labelId="demo-select-small"
                                    id="demo-select-small"
                                    value={selectedLanguage}
                                    label="Language"
                                    onChange={(e) => setSelectedLanguage(e.target.value)}
                                >
                                    {languages.map((language, index) => (
                                        <MenuItem key={index} value={language.slug}>
                                            {language.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid size={{ xs: 12 }}>
                            <Box
                                width={1}
                                height={1}
                                minHeight={500}
                                borderRadius={2}
                                border={`2px solid ${theme.palette.divider}`}
                                maxWidth={1384}
                                p={theme.spacing(2, 1)}
                                sx={{
                                    borderStyle: "dashed",
                                }}
                            >
                                <div id={EDITOR_CONTAINER_ID} className="page-template-editor"></div>
                            </Box>
                        </Grid>

                        <Grid size={{ xs: 12 }} sx={{ display: "flex", alignContent: "flex-end" }}>
                            <Button
                                variant="contained"
                                color="error"
                                sx={{ ml: "auto" }}
                                onClick={() => onCancel()}
                                disabled={isSaving}
                            >
                                Cancel
                            </Button>
                            <Box sx={{ ml: 1, position: "relative" }}>
                                <Button
                                    variant="contained"
                                    startIcon={<PaperAirplaneIcon className="h-5 w-5" />}
                                    sx={{ ml: 1 }}
                                    onClick={() => onSave()}
                                    disabled={isSaving}
                                >
                                    Save
                                </Button>
                                {isSaving && (
                                    <CircularProgress
                                        size={24}
                                        sx={{
                                            position: "absolute",
                                            top: "50%",
                                            left: "50%",
                                            marginTop: "-12px",
                                            marginLeft: "-12px",
                                        }}
                                    />
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </Box>
    );
};

export default Editor;
